import { createRouter, createWebHashHistory } from "vue-router";

import NProgress from "nprogress/nprogress.js";

// Main layouts
import LayoutBackend from "@/layouts/variations/BackendStarter.vue";
import LayoutSimple from "@/layouts/variations/Simple.vue";

import { useAuthStore } from "@/stores/auth";

const Dashboard = () => import("@/views/starter/DashboardView.vue");

// Auth
const AuthSignIn = () => import("@/views/auth/SignInView.vue");

const MailSms = () => import("@/views/pages/MailSmsView.vue");
const Customers = () => import("@/views/pages/CustomersView.vue");
const Dealers = () => import("@/views/pages/DealersView.vue");
const Applications = () => import("@/views/pages/ApplicationsView.vue");
const Settings = () => import("@/views/pages/SettingsView.vue");

const DealerDetail = () => import("@/views/pages/dealerComponents/dealer_detail.vue");
const CustomerDetail = () => import("@/views/pages/customerComponents/customer_detail.vue");
const ApplicationDetail = () => import("@/views/pages/applicationComponents/application_detail.vue");
const SourceList = () => import("@/views/pages/applicationComponents/source_list.vue");
const SourcesManager = () => import("@/views/pages/facilityComponents/sources_manager.vue");
const AIChat = () => import("@/views/pages/ai/AIChat.vue");
const Documents = () => import("@/views/pages/DocumentsView.vue");

// Errors
const Error400 = () => import("@/views/errors/400View.vue");
const Error401 = () => import("@/views/errors/401View.vue");
const Error403 = () => import("@/views/errors/403View.vue");
const Error404 = () => import("@/views/errors/404View.vue");
const Error500 = () => import("@/views/errors/500View.vue");
const Error503 = () => import("@/views/errors/503View.vue");

// Set all routes
const routes = [
  {
    path: "/",
    redirect: "/dashboard",
    component: LayoutBackend,
    meta: {
      middleware: "auth",
    },
    children: [
      {
        path: "dashboard",
        name: "backend-dashboard",
        component: Dashboard,
        meta: {
          middleware: "auth",
        },
      },
    ],
  },
  {
    path: "/pages",
    component: LayoutBackend,
    meta: {
      middleware: "auth",
    },
    children: [
      {
        path: "mailsms",
        name: "mailsms",
        component: MailSms,
        meta: {
          middleware: "auth",
        },
      },
      {
        path: "dealers",
        name: "dealers",
        component: Dealers,
        meta: {
          middleware: "auth",
        },
      },
      {
        path: "dealers/:id",
        name: "dealer-detail",
        component: DealerDetail,
        meta: {
          middleware: "auth",
        },
      },
      {
        path: "settings",
        name: "settings",
        component: Settings,
        meta: {
          middleware: "auth",
        },
      },
      {
        path: "customers",
        name: "customers",
        component: Customers,
        meta: {
          middleware: "auth",
        },
      },
      {
        path: "customers/:id",
        name: "customer-detail",
        component: CustomerDetail,
        meta: {
          middleware: "auth",
        },
      },
      {
        path: "applications",
        name: "applications",
        component: Applications,
        meta: {
          middleware: "auth",
        },
      },
      {
        path: "applications/:id",
        name: "application-detail",
        component: ApplicationDetail,
        meta: {
          middleware: "auth",
        },
      },
      {
        path: "sources/:id",
        name: "source-list",
        component: SourceList,
        meta: {
          middleware: "auth",
        },
      },
      {
        path: "sources/:id",
        name: "source-manager",
        component: SourcesManager,
        meta: {
          middleware: "auth",
        },
      },
      {
        path: "ai-chat",
        name: "backend-ai-chat",
        component: AIChat,
        meta: {
          middleware: "auth",
        },
      },
      {
        path: "documents",
        name: "documents",
        component: Documents,
        meta: {
          middleware: "auth",
        },
      },
    ],
  },
  {
    path: "/auth",
    component: LayoutSimple,
    children: [
      {
        path: "sign-in",
        name: "sign-in",
        component: AuthSignIn,
      },
    ]
  },
  {
    path: "/errors",
    component: LayoutSimple,
    children: [
      {
        path: "400",
        name: "error-400",
        component: Error400,
      },
      {
        path: "401",
        name: "error-401",
        component: Error401,
      },
      {
        path: "403",
        name: "error-403",
        component: Error403,
      },
      {
        path: "404",
        name: "error-404",
        component: Error404,
      },
      {
        path: "500",
        name: "error-500",
        component: Error500,
      },
      {
        path: "503",
        name: "error-503",
        component: Error503,
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/errors/404",
  },
];

// Create Router
const router = createRouter({
  history: createWebHashHistory(),
  linkActiveClass: "active",
  linkExactActiveClass: "active",
  scrollBehavior() {
    return { left: 0, top: 0 };
  },
  routes,
});

// NProgress
/*eslint-disable no-unused-vars*/
NProgress.configure({ showSpinner: false });

router.beforeResolve(async (to, from, next) => {

  const authStore = useAuthStore();

  if (to.name) {
    NProgress.start();
  }

  const isAuthenticated = await authStore.verifyAuth();

  if (to.name == "sign-in" && authStore.isAuthenticated) {
    next({ name: "backend-dashboard" });
  }

  if (to.meta.middleware == "auth") {
    if (authStore.isAuthenticated) {
      next();
    } else {
      next({ name: "sign-in" });
    }
  } else {
    next();
  }

});

router.onError((error) => {
  const isChunkError =
    error?.message?.includes("Failed to fetch dynamically imported module") ||
    error?.message?.includes("Unable to preload CSS") ||
    error?.message?.includes("error loading dynamically imported module");

  if (isChunkError) {
    const reloadKey = "chunk_failed_reload";
    if (!sessionStorage.getItem(reloadKey)) {
      sessionStorage.setItem(reloadKey, "true");
      window.location.reload();
    }
  }
});

router.afterEach((to, from) => {
  NProgress.done();
  sessionStorage.removeItem("chunk_failed_reload");
});
/*eslint-enable no-unused-vars*/

export default router;
